import React from "react";
import { Link } from "react-router-dom";
import { Search as SearchIcon, Shield, Network, Globe, Lock, Filter } from "lucide-react";

// Cada exemplo vira um link que abre a Busca já com a query (?q=).
function Ex({ q }) {
  return (
    <Link
      to={`/search?q=${encodeURIComponent(q)}`}
      className="inline-flex items-center gap-1 rounded bg-secondary px-2 py-0.5 font-mono text-xs text-secondary-foreground hover:bg-primary hover:text-primary-foreground"
      title="Abrir na busca"
    >
      <SearchIcon className="h-3 w-3 opacity-60" />
      {q}
    </Link>
  );
}

function Section({ icon: Icon, title, children }) {
  return (
    <div className="rounded-lg border border-border bg-card p-4">
      <div className="mb-2 flex items-center gap-2 text-sm font-semibold">
        <Icon className="h-4 w-4 text-primary" /> {title}
      </div>
      {children}
    </div>
  );
}

function Field({ name, desc, examples }) {
  return (
    <div className="border-b border-border/60 py-2 last:border-0">
      <div className="flex flex-wrap items-baseline gap-2">
        <code className="rounded bg-background px-1.5 py-0.5 font-mono text-xs text-primary">{name}</code>
        <span className="text-sm text-muted-foreground">{desc}</span>
      </div>
      {examples?.length > 0 && (
        <div className="mt-1.5 flex flex-wrap gap-1.5">
          {examples.map((q) => <Ex key={q} q={q} />)}
        </div>
      )}
    </div>
  );
}

export default function Dorks() {
  return (
    <div className="w-full space-y-6">
      <div>
        <h1 className="text-xl font-semibold">Dorks &amp; modos de busca</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          A busca aceita a sintaxe <span className="font-mono">query_string</span> do OpenSearch.
          Cada filtro (separado por <span className="font-mono">AND</span>) é roteado para o índice
          que tem aquele campo — serviços, DNS ou certificados — e os resultados são
          <b> correlacionados por host (IP)</b>: o host aparece quando satisfaz <i>todos</i> os
          filtros, mesmo que cada um venha de uma fonte diferente. Clique em qualquer exemplo
          para executá-lo.
        </p>
      </div>

      <div className="rounded-lg border border-border bg-card p-4 text-sm text-muted-foreground">
        <div className="mb-1 font-semibold text-foreground">Bom saber</div>
        <ul className="list-disc space-y-1 pl-5">
          <li>Os filtros de campo são <b>case-insensitive</b> (<span className="font-mono">city:curitiba</span> = <span className="font-mono">city:Curitiba</span>).</li>
          <li>Também são <b>insensíveis a acento</b> (<span className="font-mono">city:"sao paulo"</span> = <span className="font-mono">city:"São Paulo"</span>).</li>
          <li>Prefixe um filtro com <span className="font-mono">not</span> ou <span className="font-mono">-</span> para <b>negá-lo</b> (subtrai do resultado).</li>
          <li><span className="font-mono">campo:*</span> = "tem qualquer valor"; <span className="font-mono">-campo:*</span> = "está vazio/ausente".</li>
          <li>O resultado tem três abas: <b>Hosts</b>, <b>Certificados</b> e <b>Domínios</b> — todas para a mesma consulta.</li>
          <li>Clicar num chip/valor do resultado (porta, país, cidade, estado, nome DNS, certificado) <b>adiciona o filtro</b> à busca.</li>
          <li>Valores com espaço precisam de aspas: <span className="font-mono">city:"São Paulo"</span>.</li>
        </ul>
      </div>

      <Section icon={SearchIcon} title="Texto livre">
        <p className="text-sm text-muted-foreground">
          Sem prefixo de campo, o termo é buscado nos campos principais (hostname, serviço,
          produto, versão, banner, CN, país, cidade…).
        </p>
        <div className="mt-2 flex flex-wrap gap-1.5">
          <Ex q="nginx" /><Ex q='"Apache httpd"' /><Ex q="mikrotik" /><Ex q="sec4us.com.br" />
        </div>
      </Section>

      <Section icon={Filter} title="Operadores e curingas">
        <div className="space-y-1.5 text-sm text-muted-foreground">
          <div><b>AND / OR / NOT</b> — combinação de termos.</div>
          <div><b>( )</b> — agrupamento.</div>
          <div><b>"…"</b> — frase exata.</div>
          <div><b>* ?</b> — curingas (vários / um caractere).</div>
          <div><b>[a TO b]</b> — intervalo (ranges).</div>
        </div>
        <div className="mt-2 flex flex-wrap gap-1.5">
          <Ex q="nginx AND country:BR" />
          <Ex q="product:nginx OR product:apache" />
          <Ex q="port:443 NOT country:US" />
          <Ex q="hostname:*.sec4us.com.br" />
          <Ex q="port:[1 TO 1024]" />
        </div>
      </Section>

      <Section icon={Filter} title="Negação e presença de campo">
        <div className="space-y-1.5 text-sm text-muted-foreground">
          <div>
            <b><span className="font-mono">not campo:valor</span></b> ou <b><span className="font-mono">-campo:valor</span></b> —
            nega o filtro: <b>remove</b> do resultado os hosts que o satisfazem. Combine com filtros
            positivos via <span className="font-mono">AND</span>.
          </div>
          <div>
            <b><span className="font-mono">campo:*</span></b> — o campo <b>existe</b> (tem qualquer valor não vazio);
            <b><span className="font-mono"> -campo:*</span></b> — o campo está <b>vazio/ausente</b>.
          </div>
          <div>
            Para <span className="font-mono">cloud_product</span>/<span className="font-mono">saas_product</span>/<span className="font-mono">datacenter</span>,
            a negação considera a <b>flag efetiva</b> (a mesma do chip): valor gravado <i>ou</i>
            classificado pelo nome (ex.: <span className="font-mono">*.cloudfront.net</span>).
          </div>
        </div>
        <div className="mt-2 flex flex-wrap gap-1.5">
          <Ex q='region:"São Paulo" AND not saas_product:"TOTVS"' />
          <Ex q='region:"São Paulo" AND -cloud_product:"AWS"' />
          <Ex q='country:BR AND -cloud_product:* AND -saas_product:*' />
          <Ex q="port:443 AND cloud_product:*" />
          <Ex q="ssl:*.gov.br AND -datacenter:*" />
        </div>
      </Section>

      <Section icon={Network} title="Serviços / portas (nmap)">
        <Field name="ip:" desc="endereço IP exato" examples={["ip:1.2.3.4"]} />
        <Field name="port:" desc="porta aberta" examples={["port:443", "port:[8000 TO 9000]"]} />
        <Field name="service:" desc="nome do serviço" examples={["service:http", "service:ssh"]} />
        <Field name="product:" desc="produto detectado (-sV)" examples={['product:nginx', 'product:"MikroTik bandwidth-test server"']} />
        <Field name="version:" desc="versão do produto" examples={["version:1.18*"]} />
        <Field name="banner:" desc="banner / saída de scripts NSE (full-text)" examples={['banner:"Server: nginx"', "banner:Ubuntu"]} />
        <Field name="cpe:" desc="CPE do serviço" examples={["cpe:*mikrotik*"]} />
        <Field name="protocol:" desc="protocolo (tcp/udp)" examples={["protocol:tcp"]} />
        <Field name="hostname:" desc="hostname do host (scan)" examples={["hostname:*.amazonaws.com"]} />
      </Section>

      <Section icon={Globe} title="Geolocalização">
        <Field name="country:" desc="país (ISO-2)" examples={["country:BR", "country:US"]} />
        <Field name="region:" desc="estado / província (nome)" examples={['region:"São Paulo"', "region:Paraná"]} />
        <Field name="city:" desc="cidade" examples={['city:"São Paulo"', "city:Curitiba"]} />
      </Section>

      <Section icon={Globe} title="DNS (enumdns)">
        <Field name="fqdn:" desc="FQDN resolvido" examples={["fqdn:www.sec4us.com.br", "fqdn:*.gov.br"]} />
        <Field name="ptr:" desc="registro PTR (reverso)" examples={["ptr:*.locaweb.com.br"]} />
        <Field name="soa:" desc="SOA da zona" examples={["soa:*sec4us*"]} />
        <Field name="txt:" desc="registros TXT (full-text)" examples={["txt:*spf*"]} />
        <Field name="record_type:" desc="tipo do registro (A, AAAA, CNAME, MX, NS, SOA…)" examples={["record_type:MX", "record_type:NS"]} />
        <Field name="dns_open_resolver:" desc="servidor DNS é open resolver (true/false); detectado pelo scan nmap" examples={["dns_open_resolver:true", "dns_open_resolver:true AND country:BR"]} />
        <Field name="dns_protocol:" desc="protocolo do serviço DNS (udp/tcp)" examples={["dns_protocol:udp"]} />
        <Field name="dns_status:" desc="status da resolução DNS no scan (success/fail)" examples={["dns_status:success"]} />
        <Field name="asn:" desc="ASN do host (aceita 13335 ou AS13335); asn:* = tem ASN" examples={["asn:13335", "asn:AS16509", "asn:13335 AND port:443"]} />
        <Field name="cloud_product:" desc="produto de nuvem (enriquecimento)" examples={["cloud_product:*cloudflare*"]} />
        <Field name="saas_product:" desc="produto SaaS (enriquecimento)" examples={["saas_product:*google*"]} />
        <Field name="datacenter:" desc="datacenter (enriquecimento)" examples={["datacenter:*aws*"]} />
        <Field name="cloud_provider:" desc="provedor de cloud (cloud_data, casado por DNS/IP)" examples={['cloud_provider:Amazon', "cloud_provider:*"]} />
        <Field name="cloud_region:" desc="região do cloud" examples={["cloud_region:us-east-1", 'cloud_region:sa-east-1']} />
        <Field name="cloud_service:" desc="serviço do cloud" examples={["cloud_service:EC2"]} />
      </Section>

      <Section icon={Lock} title="Certificados TLS (certcrawler)">
        <p className="mb-2 text-sm text-muted-foreground">
          A dork <code className="font-mono text-primary">ssl:</code> busca em todos os campos do
          certificado (CN, subject, issuer e SAN) de uma vez e correlaciona aos hosts que o servem.
        </p>
        <Field name="ssl:" desc="qualquer campo do certificado (CN/subject/issuer/SAN)" examples={["ssl:sec4us.com.br", "ssl:*.sec4us.com.br", 'ssl:"Terra Networks Brasil LTDA"']} />
        <Field name="cn:" desc="Common Name do certificado" examples={["cn:terra.com.br"]} />
        <Field name="san:" desc="Subject Alternative Name" examples={["san:*.terra.com.br"]} />
        <Field name="issuer:" desc="emissor" examples={['issuer:"GlobalSign"']} />
        <Field name="subject:" desc="subject (DN completo)" examples={["subject:*Brasil*"]} />
        <Field name="sni:" desc="SNI usado na coleta (vhost); sni vazio = requisição padrão" examples={["sni:*.sec4us.com.br"]} />
        <Field name="title:" desc="título (<title>) da resposta HTTP por SNI" examples={['title:"Acesso restrito"', "title:*login*"]} />
        <Field name="banner:" desc="cabeçalho HTTP por SNI (certcrawler) e banner/scripts do nmap" examples={['banner:"Server: nginx"', "banner:cloudflare"]} />
      </Section>

      <Section icon={Shield} title="Exemplos combinados">
        <div className="flex flex-wrap gap-1.5">
          <Ex q="ssl:*.sec4us.com.br AND port:443" />
          <Ex q="country:BR AND product:nginx" />
          <Ex q='service:http AND banner:"401 Unauthorized"' />
          <Ex q="port:2000 AND product:mikrotik" />
          <Ex q='region:"São Paulo" AND port:443' />
          <Ex q="cloud_product:*cloudflare* AND ssl:*.gov.br" />
          <Ex q='city:"sao paulo" AND not cloud_product:*' />
          <Ex q="dns_open_resolver:true AND country:BR" />
        </div>
      </Section>
    </div>
  );
}
